import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { switchMap } from "rxjs/operators";
import {
  YOUSIGN_SIGNATURE_NAME_KEYS,
  YousignFlowType,
  YousignHttpService
} from "app/shared/services/yousign/yousign.http.service";
import { YousignService } from "app/shared/services/yousign/YousignService";
import { environment } from "environments/environment";

//YousignComponent based on https://developers.yousign.com/docs/iframe-advanced

declare const Yousign: any;

interface YousignInitResponse {
  signature_link?: string;
  sign_with_spouse?: boolean;
  spouse_signature_link?: string;
}

interface YousignStatusResponse {
  applicant_signed?: boolean;
  signed?: boolean;
}

export type YousignSignatureStep = "single" | "joint-applicant" | "joint-spouse";

@Component({
  selector: "app-yousign",
  templateUrl: "./yousign.component.html",
  styleUrls: ["./yousign.component.scss"],
  standalone: false
})
export class YousignComponent implements OnInit, OnDestroy {
  @Input() statementId: number;
  @Input() flowType: YousignFlowType = "vollmacht";
  @Input() mockSignWithSpouse = false;
  @Input() isVerified: boolean = false;
  @Output() isVerifiedChange = new EventEmitter<boolean>();
  @Output() signatureStepChange = new EventEmitter<YousignSignatureStep>();

  private instance: any;
  private statusInterval: ReturnType<typeof setInterval>;
  private spouseSignatureLink: string | null = null;
  private signWithSpouse = false;
  private isSpouseSignatureStepActive = false;
  private isApplicantSigned = false;

  constructor(
    private yousignService: YousignService,
    private yousignHttp: YousignHttpService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.isMockSignedStatusEnabled() && this.flowType === "payment-instruction") {
      this.initPaymentInstructionMock();
      return;
    }

    this.yousignService.loadYousign(this.statementId).subscribe({
      next: () => {
        this.initYousign();
      },
      error: (err) => {
        console.error("Yousign init error", err);
        this.handleInitErrorInMockMode();
      }
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.statusInterval);
  }

  public initYousign(): void {
    this.translate
      .get(YOUSIGN_SIGNATURE_NAME_KEYS[this.flowType])
      .pipe(
        switchMap((signatureName: string) =>
          this.yousignHttp.initYousign(this.statementId, this.flowType, signatureName)
        )
      )
      .subscribe({
        next: (response: any) => {
          const initResponse = response as YousignInitResponse;

          if (!initResponse?.signature_link) {
            console.error("There is no signature_link in response");
            this.handleInitErrorInMockMode();
            return;
          }

          this.signWithSpouse = !!initResponse.sign_with_spouse;
          this.spouseSignatureLink = initResponse.spouse_signature_link || null;
          this.isApplicantSigned = false;

          if (this.isMockSignedStatusEnabled()) {
            this.isSpouseSignatureStepActive = false;
            this.emitCurrentStep();
            this.renderYousignIframe(initResponse.signature_link);
            return;
          }

          this.yousignHttp.yousignCheckStatus(this.statementId, this.flowType).subscribe({
            next: (statusResponse: any) => {
              const parsedStatusResponse = statusResponse as YousignStatusResponse;

              if (parsedStatusResponse?.signed) {
                this.markAsVerified();
                return;
              }

              if (this.shouldUnlockProceedForSpouseStep(parsedStatusResponse)) {
                this.markApplicantAsSigned();
              }

              this.isSpouseSignatureStepActive = false;
              this.emitCurrentStep();
              this.renderYousignIframe(initResponse.signature_link);
            },
            error: () => {
              this.isSpouseSignatureStepActive = false;
              this.emitCurrentStep();
              this.renderYousignIframe(initResponse.signature_link);
            }
          });
        },
        error: (err) => {
          console.error("Yousign init error", err);
          this.handleInitErrorInMockMode();
        }
      });
  }

  public checkVerificationStatus(): void {
    if (this.isMockSignedStatusEnabled()) {
      this.handleMockCheckStatus();
      return;
    }

    this.yousignHttp.yousignCheckStatus(this.statementId, this.flowType).subscribe({
      next: (response: any) => {
        const parsedResponse = response as YousignStatusResponse;

        if (parsedResponse.signed) {
          this.markAsVerified();
          return;
        }

        if (this.shouldUnlockProceedForSpouseStep(parsedResponse)) {
          this.markApplicantAsSigned();
        }
      },
      error: (err) => {
        console.error("Yousign init error", err);
      }
    });
  }

  public markAsVerified(): void {
    this.isVerified = true;
    this.isApplicantSigned = true;
    clearInterval(this.statusInterval);
    this.isVerifiedChange.emit(true);
  }

  public goToSpouseSignatureStep(): void {
    if (!this.signWithSpouse || this.isSpouseSignatureStepActive || !this.isApplicantSigned) {
      return;
    }

    this.isVerified = false;
    this.isVerifiedChange.emit(false);
    this.openSpouseSignature();
  }

  private shouldUnlockProceedForSpouseStep(response: YousignStatusResponse): boolean {
    return (
      this.signWithSpouse &&
      !this.isSpouseSignatureStepActive &&
      !!response?.applicant_signed &&
      !response?.signed &&
      !!this.spouseSignatureLink
    );
  }

  private openSpouseSignature(): void {
    if (!this.spouseSignatureLink && !this.isMockSignedStatusEnabled()) {
      return;
    }

    this.isSpouseSignatureStepActive = true;
    this.emitCurrentStep();

    if (this.spouseSignatureLink) {
      this.renderYousignIframe(this.spouseSignatureLink);
      return;
    }

    this.startStatusPolling();
  }

  private emitCurrentStep(): void {
    if (!this.signWithSpouse) {
      this.signatureStepChange.emit("single");
      return;
    }

    this.signatureStepChange.emit(this.isSpouseSignatureStepActive ? "joint-spouse" : "joint-applicant");
  }

  private markApplicantAsSigned(): void {
    if (this.isApplicantSigned) {
      return;
    }

    this.isApplicantSigned = true;
    this.isVerified = true;
    clearInterval(this.statusInterval);
    this.isVerifiedChange.emit(true);
  }

  private renderYousignIframe(signatureLink: string): void {
    if (!this.isYousignGlobalAvailable()) {
      this.handleInitErrorInMockMode();
      return;
    }

    this.stopStatusPolling();
    this.clearYousignContainer();

    this.instance = new Yousign({
      signatureLink,
      iframeContainerId: "yousign-container",
      isSandbox: true
    });

    this.instance.onStarted((e: any) => console.log("started", e));
    this.instance.onSuccess((e: any) => {
      console.log("onSuccess");
      this.startStatusPolling();
    });
    this.instance.onError((e: any) => console.error("error", e));

    this.startStatusPolling();

    const container = document.getElementById("yousign-iframe");
    if (container) {
      container.style.height = `100%`;
      container.style.width = `100%`;
    }
  }

  private clearYousignContainer(): void {
    const container = document.getElementById("yousign-container");
    if (container) {
      container.innerHTML = "";
    }
  }

  private startStatusPolling(): void {
    this.stopStatusPolling();
    this.statusInterval = setInterval(() => this.checkVerificationStatus(), 800);
  }

  private stopStatusPolling(): void {
    clearInterval(this.statusInterval);
  }

  private isMockSignedStatusEnabled(): boolean {
    return !environment.production && environment.mockYousignSignedStatus;
  }

  private handleInitErrorInMockMode(): void {
    if (!this.isMockSignedStatusEnabled()) {
      return;
    }

    this.isSpouseSignatureStepActive = false;
    this.emitCurrentStep();
    this.renderMockIframe();
    this.startStatusPolling();
  }

  private isYousignGlobalAvailable(): boolean {
    return typeof Yousign !== "undefined";
  }

  private handleMockCheckStatus(): void {
    if (this.signWithSpouse && !this.isSpouseSignatureStepActive) {
      this.markApplicantAsSigned();
      return;
    }

    this.markAsVerified();
  }

  private initPaymentInstructionMock(): void {
    this.signWithSpouse = !!this.mockSignWithSpouse;
    this.spouseSignatureLink = null;
    this.isApplicantSigned = false;
    this.isSpouseSignatureStepActive = false;
    this.emitCurrentStep();
    this.renderMockIframe();
    this.startStatusPolling();
  }

  private renderMockIframe(): void {
    this.clearYousignContainer();

    const container = document.getElementById("yousign-container");
    if (!container) {
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", "Mock YouSign");
    iframe.setAttribute("frameborder", "0");
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.minHeight = "520px";
    iframe.style.border = "1px solid #d9e3e6";
    iframe.style.borderRadius = "8px";
    iframe.style.background = "#fff";
    iframe.srcdoc = `
      <html>
        <body style="margin:0;font-family:Arial,sans-serif;background:#f5f8fa;color:#17323d;">
          <div style="display:flex;align-items:center;justify-content:center;height:100%;">
            <div style="text-align:center;padding:24px;">
              <h3 style="margin:0 0 12px 0;">YouSign mock preview</h3>
              <p style="margin:0;">Mock signature is enabled. Continue in the app to proceed.</p>
            </div>
          </div>
        </body>
      </html>
    `;

    container.appendChild(iframe);

    const wrapper = document.getElementById("yousign-iframe");
    if (wrapper) {
      wrapper.style.height = "100%";
      wrapper.style.width = "100%";
    }
  }
}
